import { useState, useEffect } from 'react';
import { fetchGoalsByType, StudyGoal, GoalType } from '../../../hooks/useStudyGoals';

const GOAL_TYPE_LABELS: Record<GoalType, string> = {
  daily: '今日の目標',
  weekly: '今週の目標',
  monthly: '今月の目標',
  quarterly: '四半期目標',
  yearly: '年間目標',
};

const GOAL_TYPE_ORDER: GoalType[] = ['daily', 'weekly', 'monthly', 'quarterly', 'yearly'];

interface StudyGoalProgressWidgetProps {
  layoutMode?: 'normal' | 'compact';
}

export default function StudyGoalProgressWidget({ layoutMode = 'normal' }: StudyGoalProgressWidgetProps) {
  const [goalsByType, setGoalsByType] = useState<Record<GoalType, StudyGoal[]>>({
    daily: [],
    weekly: [],
    monthly: [],
    quarterly: [],
    yearly: [],
  });
  const [loading, setLoading] = useState(true);
  const [animatedProgress, setAnimatedProgress] = useState<Record<string, number>>({});

  useEffect(() => {
    const loadGoals = async () => {
      setLoading(true);
      const results = await Promise.all(
        GOAL_TYPE_ORDER.map((type) => fetchGoalsByType(type))
      );
      const mapped: Record<GoalType, StudyGoal[]> = {
        daily: results[0],
        weekly: results[1],
        monthly: results[2],
        quarterly: results[3],
        yearly: results[4],
      };
      setGoalsByType(mapped);
      setLoading(false);
    };
    loadGoals();
  }, []);

  const allGoals = GOAL_TYPE_ORDER.flatMap((type) => goalsByType[type]);

  useEffect(() => {
    if (!loading && allGoals.length > 0) {
      const initial: Record<string, number> = {};
      allGoals.forEach((g) => { initial[g.id] = 0; });
      setAnimatedProgress(initial);

      setTimeout(() => {
        const actual: Record<string, number> = {};
        allGoals.forEach((g) => {
          actual[g.id] = g.is_completed ? 100 : 0;
        });
        setAnimatedProgress(actual);
      }, 100);
    }
  }, [loading]);

  if (loading) {
    return (
      <div className="bg-white rounded-2xl shadow-sm p-6">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-8 h-8 flex items-center justify-center">
            <i className="ri-trophy-line text-xl text-amber-500"></i>
          </div>
          <h3 className="text-lg font-semibold text-gray-800">学習目標達成率</h3>
        </div>
        <div className="text-center py-8 text-gray-400 text-sm">読み込み中...</div>
      </div>
    );
  }

  if (allGoals.length === 0) {
    return (
      <div className="bg-white rounded-2xl shadow-sm p-6">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-8 h-8 flex items-center justify-center">
            <i className="ri-trophy-line text-xl text-amber-500"></i>
          </div>
          <h3 className="text-lg font-semibold text-gray-800">学習目標達成率</h3>
        </div>
        <div className="text-center py-8 text-gray-400 text-sm">
          目標が設定されていません
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white dark:bg-gray-800 rounded-xl shadow-lg ${layoutMode === 'compact' ? 'p-4' : 'p-6'}`}>
      <h3 className={`font-bold text-gray-800 dark:text-white mb-4 flex items-center gap-2 ${layoutMode === 'compact' ? 'text-base' : 'text-lg'}`}>
        <i className="ri-target-line text-red-600"></i>
        学習目標達成率
      </h3>

      {loading ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
        </div>
      ) : (
        <div className={`space-y-4 ${layoutMode === 'compact' ? 'text-sm' : ''}`}>
          {GOAL_TYPE_ORDER.map((type) => {
            const goals = goalsByType[type];
            if (goals.length === 0) return null;

            const completedCount = goals.filter((g) => g.is_completed).length;
            const typeProgress = goals.length > 0
              ? Math.round((completedCount / goals.length) * 100)
              : 0;

            return (
              <div key={type}>
                <div className="flex items-center justify-between mb-2">
                  <span className={`text-xs font-semibold text-gray-500 uppercase tracking-wide ${layoutMode === 'compact' ? 'text-xs' : ''}`}>
                    {GOAL_TYPE_LABELS[type]}
                  </span>
                  <span className={`text-xs font-bold text-teal-600 ${layoutMode === 'compact' ? 'text-xs' : ''}`}>
                    {completedCount}/{goals.length} 達成
                  </span>
                </div>

                {/* Type-level progress bar */}
                <div className="relative h-2 bg-gray-100 rounded-full overflow-hidden mb-3">
                  <div
                    className={`absolute top-0 left-0 h-full rounded-full transition-all duration-1000 ease-out ${
                      typeProgress >= 100
                        ? 'bg-gradient-to-r from-green-400 to-green-500'
                        : 'bg-gradient-to-r from-teal-400 to-teal-500'
                    }`}
                    style={{ width: `${typeProgress}%` }}
                  >
                    <div className="absolute inset-0 bg-white/20"></div>
                  </div>
                </div>

                {/* Individual goals */}
                <div className={`space-y-2 ${layoutMode === 'compact' ? 'space-y-1' : ''}`}>
                  {goals.map((goal) => {
                    const progress = animatedProgress[goal.id] ?? 0;
                    return (
                      <div
                        key={goal.id}
                        className={`flex items-center gap-3 px-3 py-2 rounded-xl bg-gray-50 ${layoutMode === 'compact' ? 'text-xs' : ''}`}
                      >
                        <div className={`w-5 h-5 flex items-center justify-center rounded-full flex-shrink-0 ${
                          goal.is_completed
                            ? 'bg-green-100 text-green-600'
                            : 'bg-gray-200 text-gray-400'
                        }`}>
                          <i className={`text-xs ${goal.is_completed ? 'ri-check-line' : 'ri-time-line'}`}></i>
                        </div>
                        <span className={`text-sm flex-1 ${
                          goal.is_completed ? 'line-through text-gray-400' : 'text-gray-700'
                        }`}>
                          {goal.goal_text}
                        </span>
                        {goal.is_completed && (
                          <span className="text-xs font-medium text-green-600 bg-green-50 px-2 py-0.5 rounded-full">
                            完了
                          </span>
                        )}
                        {/* Mini progress bar per goal */}
                        <div className="w-16 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all duration-1000 ease-out ${
                              goal.is_completed
                                ? 'bg-green-400'
                                : 'bg-teal-300'
                            }`}
                            style={{ width: `${progress}%` }}
                          ></div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Overall summary */}
      {(() => {
        const total = allGoals.length;
        const completed = allGoals.filter((g) => g.is_completed).length;
        const overall = total > 0 ? Math.round((completed / total) * 100) : 0;
        return (
          <div className={`mt-6 pt-4 border-t border-gray-100 ${layoutMode === 'compact' ? 'mt-4 pt-4' : ''}`}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold text-gray-700">総合達成率</span>
              <span className="text-lg font-bold text-teal-600">{overall}%</span>
            </div>
            <div className="relative h-3 bg-gray-100 rounded-full overflow-hidden">
              <div
                className={`absolute top-0 left-0 h-full rounded-full transition-all duration-1000 ease-out ${
                  overall >= 100
                    ? 'bg-gradient-to-r from-green-400 to-green-500'
                    : 'bg-gradient-to-r from-teal-400 to-cyan-400'
                }`}
                style={{ width: `${overall}%` }}
              >
                <div className="absolute inset-0 bg-white/20 animate-pulse"></div>
              </div>
            </div>
            <p className="text-xs text-gray-400 mt-1 text-right">
              {completed} / {total} 目標達成
            </p>
          </div>
        );
      })()}

      <style>{`
        @keyframes progressAnimation {
          from {
            width: 0%;
          }
        }
      `}</style>
    </div>
  );
}
